/**
 * scoring.js — Scoring prospect unifie (source de verite unique)
 *
 * ES module pur, zero import interne : importable a la fois par le front
 * Vite (src/App.jsx) et par les Cloudflare Pages Functions
 * (functions/api/prospection-sirene.js) sans duplication de logique.
 *
 * Modele : score base 0-100 = moyenne ponderee des sous-scores disponibles
 * (composantes manquantes ignorees + poids renormalise), puis multiplicateur
 * relation selon la phase commerciale (Phase 1 compte ouvert / Phase 2
 * prospect / Phase 3 reseau).
 *
 * Ponderation effectif/conso ajustee (2026-07-20, validation Wissam) :
 * un TPE (1-5 salaries) dans un secteur Tier 1 (opticien, comptable,
 * medical...) reste un prospect fort malgre un effectif faible — ces
 * secteurs vendent au volume/patientele, pas a la taille de bureau. Sans
 * plancher, la composante effectif ecrasait injustement le score sectoriel
 * fort (cas ACUITIS : NAF opticien 86 + effectif 2 salaries -> P2 au lieu
 * de P1). De meme, un CA parc deja observe sur un compte ouvert (Phase 1)
 * est une preuve mesuree, pas une estimation prospective : il merite un
 * plancher plus haut que la meme donnee sur un simple prospect.
 */

export const DEFAULT_SECTEUR = { secteur: 'Autre / non qualifie', score: 50 };

// NAF (APE rev.2) normalise sans separateur (ex "6920Z") -> secteur + propension 0-100.
// Extensible librement. Tout NAF absent -> DEFAULT_SECTEUR (50).
export const NAF_SECTEURS = {
  // — Tier 1 (80-95) —
  '6920Z': { secteur: 'Expert-comptable / fiduciaire', score: 95 },
  '6910Z': { secteur: 'Juridique (avocat/notaire/huissier)', score: 90 },
  '8621Z': { secteur: 'Cabinet medical', score: 88 },
  '8622A': { secteur: 'Medecin specialiste', score: 88 },
  '8622B': { secteur: 'Medecin specialiste', score: 88 },
  '8622C': { secteur: 'Medecin specialiste', score: 88 },
  '8623Z': { secteur: 'Cabinet dentaire', score: 84 },
  '8690B': { secteur: "Labo d'analyses medicales", score: 84 },
  '8690D': { secteur: 'Parametical', score: 78 },
  '8690E': { secteur: 'Parametical', score: 78 },
  '8690F': { secteur: 'Parametical', score: 78 },
  '4778A': { secteur: 'Opticien', score: 86 },
  '6622Z': { secteur: 'Assurance / courtier', score: 82 },
  '6512Z': { secteur: 'Assurance', score: 78 },
  '8610Z': { secteur: 'Hopital / clinique', score: 80 },
  '4711F': { secteur: 'Hypermarche (FG interne)', score: 82 },
  '4711D': { secteur: 'Supermarche (FG interne)', score: 78 },
  // — Tier 2 (65-79) —
  '6831Z': { secteur: 'Agence immobiliere', score: 78 },
  '6832A': { secteur: 'Administration de biens', score: 76 },
  '6832B': { secteur: 'Syndic / gestion immo', score: 76 },
  '7500Z': { secteur: 'Veterinaire', score: 74 },
  '8553Z': { secteur: 'Auto-ecole', score: 72 },
  '7111Z': { secteur: 'Architecte', score: 70 },
  '7112A': { secteur: 'Geometre', score: 68 },
  '7112B': { secteur: "Bureau d'etudes / ingenierie", score: 68 },
  '7911Z': { secteur: 'Agence de voyage', score: 68 },
  '7912Z': { secteur: 'Voyagiste', score: 66 },
  '7820Z': { secteur: 'Travail temporaire (interim)', score: 68 },
  '7810Z': { secteur: 'Recrutement / placement', score: 66 },
  '6419Z': { secteur: 'Banque de proximite', score: 70 },
  '4711C': { secteur: 'Superette (FG interne)', score: 70 },
  '4773Z': { secteur: 'Pharmacie', score: 66 },
  '4645Z': { secteur: 'Gros parfumerie / beaute', score: 66 },
  // — Tier 3 (50-64) —
  '4520A': { secteur: 'Garage / entretien auto', score: 62 },
  '4520B': { secteur: 'Carrosserie', score: 60 },
  '4511Z': { secteur: 'Concession automobile', score: 62 },
  '8559A': { secteur: 'Formation continue', score: 62 },
  '8559B': { secteur: 'Enseignement / formation', score: 60 },
  '8520Z': { secteur: 'Enseignement primaire', score: 60 },
  '8531Z': { secteur: 'Enseignement secondaire', score: 60 },
  '8411Z': { secteur: 'Collectivite / mairie', score: 60 },
  '4775Z': { secteur: 'Parfumerie / cosmetique (detail)', score: 60 },
  '9602A': { secteur: 'Coiffure', score: 55 },
  '9602B': { secteur: 'Soins de beaute', score: 55 },
  '5510Z': { secteur: 'Hotel', score: 54 },
  '5610A': { secteur: 'Restaurant', score: 54 },
  '5610C': { secteur: 'Restauration rapide', score: 52 },
  '9412Z': { secteur: 'Organisation professionnelle', score: 54 },
  '9499Z': { secteur: 'Association', score: 52 },
  '7311Z': { secteur: 'Agence de publicite', score: 50 },
  // — Tier 4 (35-49) : levier etiquettes/expedition plutot que cartouche —
  '5210B': { secteur: 'Entreposage / logistique', score: 48 },
  '4941A': { secteur: 'Transport routier', score: 46 },
  '7010Z': { secteur: 'Siege social / holding', score: 45 },
  '7022Z': { secteur: 'Conseil de gestion', score: 42 },
  '1812Z': { secteur: 'Imprimerie', score: 40 },
  '1813Z': { secteur: 'Prepresse', score: 40 },
  '6201Z': { secteur: 'Developpement informatique', score: 38 },
};

// Tranche INSEE trancheEffectifsEtablissement -> sous-score 0-100
//
// 'NN' (unite non employeuse) VAUT 8, comme '00' — correctif audit prod
// 2026-08-18. Avant, 'NN' etait absent de cette table et le libelle
// "Non employeur" ne contient aucun chiffre exploitable : la composante
// effectif etait donc silencieusement SUPPRIMEE du calcul, et le score
// retombait sur secteur+zone seuls (96 sur 6920Z). C'etait le mecanisme
// reel derriere les "Non employeur en P1" — 78 % des etablissements
// 6920Z du Rhone sont dans ce cas.
const EFFECTIF_CODE = {
  '53': 100, '52': 100, '51': 98, '42': 96, '41': 94,
  '32': 92, '31': 86, '22': 82, '21': 78,
  '12': 65, '11': 50, '03': 35, '02': 22, '01': 12, '00': 8, 'NN': 8,
};

const POIDS = { secteur: 45, effectif: 20, zone: 10, conso: 25 };
const RELATION_MULT = { 1: 1.25, 2: 1.0, 3: 0.85 };

// Plancher effectif applique aux secteurs Tier 1 (score >= 80) : un TPE
// dans un secteur a forte propension ne doit pas tomber sous ce seuil.
//
// Ne s'applique QU'A PARTIR de 1 salarie (correctif audit prod 2026-08-18) :
// calibre pour les TPE ACTIVES (opticien ACUITIS, 2 salaries), il profitait
// aussi aux holdings/domiciliations sans activite, qui remontaient a 96 sur
// 6920Z. Un non-employeur garde son score brut — l'absence de salarie est un
// signal reel de non-consommation, pas un artefact de taille.
const EFFECTIF_FLOOR_TIER1 = 40;
const SECTEUR_TIER1_THRESHOLD = 80;

// Codes INSEE trancheEffectifsEtablissement sans salarie :
//   '00' = 0 salarie ; 'NN' = unite non employeuse / effectif inconnu.
const EFFECTIF_CODES_NON_EMPLOYEUR = new Set(['00', 'NN']);

// Plancher conso applique en Phase 1 (compte ouvert / recurrent) : un CA
// deja observe est une preuve, pas une estimation.
const CONSO_FLOOR_PHASE1 = 60;

// Coeur de tournee (autour de Lyon) = accessibilite max
const DEPT_COEUR = new Set(['69', '01', '38', '42', '73', '74', '07', '26', '43', '63', '03', '15']);
// Reste du territoire (AURA + Grand Est + PACA + BFC + Corse)
const DEPT_TERRITOIRE = new Set([
  '01', '03', '07', '15', '26', '38', '42', '43', '63', '69', '73', '74', // AURA
  '08', '10', '51', '52', '54', '55', '57', '67', '68', '88',            // Grand Est
  '04', '05', '06', '13', '83', '84',                                    // PACA
  '21', '25', '39', '58', '70', '71', '89', '90',                        // BFC
  '20',                                                                  // Corse (CP 20xxx)
]);

function normNaf(naf = '') {
  return String(naf).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function secteurDeNaf(naf) {
  return NAF_SECTEURS[normNaf(naf)] || DEFAULT_SECTEUR;
}

function effectifScoreRaw(p) {
  if (p && p.effectifCode && EFFECTIF_CODE[String(p.effectifCode)] != null) {
    return EFFECTIF_CODE[String(p.effectifCode)];
  }
  const label = (p && p.effectifLabel) || '';
  const m = label.match(/\d+/); // 1ere borne du libelle ("20-49…", "10 a 19…")
  if (!m) return null; // inconnu -> composante ignoree
  const n = parseInt(m[0], 10);
  if (n >= 1000) return 100; if (n >= 500) return 96; if (n >= 250) return 92;
  if (n >= 200) return 86; if (n >= 100) return 82; if (n >= 50) return 78;
  if (n >= 20) return 65; if (n >= 10) return 50; if (n >= 6) return 35;
  if (n >= 3) return 22; if (n >= 1) return 12; return 8;
}

/**
 * Etablissement sans salarie. Le code INSEE fait foi quand il est present ;
 * sinon repli sur le libelle ("Non employeur", "0 salarie").
 */
function isNonEmployeur(p) {
  const code = (p && p.effectifCode != null) ? String(p.effectifCode).trim().toUpperCase() : '';
  if (code) return EFFECTIF_CODES_NON_EMPLOYEUR.has(code);
  const label = ((p && p.effectifLabel) || '').toLowerCase();
  if (/non\s*employeur/.test(label)) return true;
  const m = label.match(/\d+/);
  return m ? parseInt(m[0], 10) === 0 : false;
}

function effectifScore(p, secScore) {
  const raw = effectifScoreRaw(p);
  if (raw == null) return null;
  // Plancher reserve aux etablissements ayant au moins 1 salarie.
  if (isNonEmployeur(p)) return raw;
  return secScore >= SECTEUR_TIER1_THRESHOLD ? Math.max(raw, EFFECTIF_FLOOR_TIER1) : raw;
}

function zoneScore(p) {
  const siret = (p && p.siret) || '';
  if (/^LU/i.test(siret) || (p && p.pays === 'LU')) return 85; // Luxembourg : couvert mais eloigne
  const cp = (p && p.codePostal) || '';
  const dept = cp.slice(0, 2);
  if (!dept) return 45;
  if (DEPT_COEUR.has(dept)) return 100;
  if (DEPT_TERRITOIRE.has(dept)) return 75;
  return 45;
}

function consoScore(p, phase) {
  const ca = p && Number(p.caEstimeParcMensuel);
  if (!ca || ca <= 0) return null; // pas de signal -> composante ignoree
  let raw;
  if (ca >= 500) raw = 100;
  else if (ca >= 300) raw = 85;
  else if (ca >= 150) raw = 65;
  else if (ca >= 80) raw = 45;
  else raw = 25;
  return phase === 1 ? Math.max(raw, CONSO_FLOOR_PHASE1) : raw;
}

function derivePhase(p) {
  const s = p && p.statut;
  if (s === 'compte_ouvert' || s === 'recurrent') return 1;
  if (p && p.reseau === true) return 3; // tag reseau/franchise (champ futur, absent du modele actuel)
  return 2;
}

/**
 * Score unifie d'un prospect / etablissement.
 * @param {object} p prospect/etablissement — champs lus : nafCode|naf,
 *   effectifCode, effectifLabel, codePostal, siret, pays, caEstimeParcMensuel,
 *   statut, reseau, secteurScoreOverride, secteurLabel
 * @returns {{score:number, secteur:string, priorite:'P1'|'P2'|'P3', phase:1|2|3, raisons:string[]}}
 */
export function scoreProspect(p = {}) {
  // override secteur (ex dataset Luxembourg sans NAF) : p.secteurScoreOverride (number) + p.secteurLabel
  const sec = (typeof p.secteurScoreOverride === 'number')
    ? { secteur: p.secteurLabel || 'Secteur (LU)', score: p.secteurScoreOverride }
    : secteurDeNaf(p.nafCode || p.naf);

  const phase = derivePhase(p);

  const comps = [
    { w: POIDS.secteur, v: sec.score },
    { w: POIDS.effectif, v: effectifScore(p, sec.score) },
    { w: POIDS.zone, v: zoneScore(p) },
    { w: POIDS.conso, v: consoScore(p, phase) },
  ].filter((c) => c.v != null);

  const wsum = comps.reduce((a, c) => a + c.w, 0) || 1;
  const base = comps.reduce((a, c) => a + c.w * c.v, 0) / wsum;

  const score = Math.round(Math.max(0, Math.min(100, base * RELATION_MULT[phase])));
  const priorite = score >= 80 ? 'P1' : score >= 60 ? 'P2' : 'P3';

  const raisons = [`Secteur ${sec.secteur} (${sec.score})`];
  const eff = effectifScore(p, sec.score);
  if (eff != null) raisons.push(`Effectif -> ${eff}`);
  const z = zoneScore(p);
  raisons.push(z >= 100 ? 'Coeur de tournee' : z >= 75 ? 'Territoire' : z === 85 ? 'Luxembourg' : 'Hors zone');
  const cs = consoScore(p, phase);
  if (cs != null) raisons.push(`Conso ${p.caEstimeParcMensuel} €/mois`);
  raisons.push(phase === 1 ? 'Phase 1 — compte ouvert (×1.25)' : phase === 3 ? 'Phase 3 — reseau (×0.85)' : 'Phase 2 — prospect');

  return { score, secteur: sec.secteur, priorite, phase, raisons };
}
